<script lang="ts">
  import { onMount } from 'svelte';
  import type { IngressConnectionStatusEntry, IngressConnectionSummary, IngressConnectionType } from '@ucc/shared';
  import {
    ApiError,
    clearIngressConnectionStatusLog,
    createIngressConnection,
    deleteIngressConnection,
    fetchIngressConnection,
    fetchIngressConnectionStatusLog,
    fetchIngressConnections,
    updateIngressConnection,
  } from '$lib/api';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  const DEFAULT_MQTT_PORT = 8883;

  let topTab = $state<IngressConnectionType>('https');
  let connections = $state<IngressConnectionSummary[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);

  let editingId = $state<string | null>(null);
  let editingInUse = $state(false);
  let statusEntries = $state<IngressConnectionStatusEntry[]>([]);
  let statusLoading = $state(false);
  let statusClearing = $state(false);
  let statusError = $state<string | null>(null);

  let name = $state('');
  let host = $state('');
  let port = $state<number | ''>(DEFAULT_MQTT_PORT);
  let clientId = $state('');
  let qos = $state<0 | 1 | 2>(1);
  let username = $state('');
  let password = $state('');
  let caCert = $state('');
  let clientCert = $state('');
  let clientKey = $state('');

  let saving = $state(false);
  let formError = $state<string | null>(null);
  let formOk = $state<string | null>(null);
  let busyDeleteId = $state<string | null>(null);
  let deleteError = $state<string | null>(null);

  const currentList = $derived(connections.filter((c) => c.type === topTab));
  const editingConnection = $derived(connections.find((c) => c.id === editingId) ?? null);
  /** MQTT/MQTTS are singleton by design — only HTTPS (or an unexpected 2nd broker) shows the list picker. */
  const showList = $derived(topTab === 'https' || currentList.length > 1);
  /** Singleton MQTT/MQTTS case: Setting stacked above its status log, no list at all. */
  const singleColumn = $derived(!showList);

  onMount(() => {
    void load();
  });

  // Only one MQTT/MQTTS connection is ever expected — jump straight to its
  // settings/status view instead of making staff click a list of one.
  $effect(() => {
    if (topTab !== 'https' && currentList.length === 1 && editingId !== currentList[0].id) {
      void selectConnection(currentList[0]);
    }
  });

  async function load() {
    loading = true;
    error = null;
    try {
      const res = await fetchIngressConnections();
      connections = res.connections;
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Failed to load connections';
    } finally {
      loading = false;
    }
  }

  function selectTab(tab: IngressConnectionType) {
    topTab = tab;
    resetForm();
  }

  function resetForm() {
    editingId = null;
    editingInUse = false;
    statusEntries = [];
    name = '';
    host = '';
    port = DEFAULT_MQTT_PORT;
    clientId = '';
    qos = 1;
    username = '';
    password = '';
    caCert = '';
    clientCert = '';
    clientKey = '';
    formError = null;
    formOk = null;
  }

  function endpointUrl(slug: string): string {
    return `${window.location.origin}/ingress/https/${slug}/:uid`;
  }

  async function selectConnection(c: IngressConnectionSummary) {
    resetForm();
    editingId = c.id;
    name = c.name;
    host = c.host ?? '';
    port = c.port ?? DEFAULT_MQTT_PORT;
    clientId = c.clientId ?? '';
    qos = (c.qos as 0 | 1 | 2) ?? 1;
    username = c.username ?? '';

    try {
      const res = await fetchIngressConnection(c.id);
      editingInUse = res.inUse;
    } catch {
      editingInUse = false;
    }

    await refreshStatusLog();
  }

  async function refreshStatusLog() {
    if (!editingId) return;
    statusError = null;
    statusLoading = true;
    try {
      const res = await fetchIngressConnectionStatusLog(editingId);
      statusEntries = res.entries;
    } catch {
      statusEntries = [];
    } finally {
      statusLoading = false;
    }
  }

  async function onClearStatusLog() {
    if (!editingId || !editingConnection) return;
    if (!confirm(`Permanently delete the status log for ${editingConnection.name}? This cannot be undone.`)) return;
    statusError = null;
    statusClearing = true;
    try {
      await clearIngressConnectionStatusLog(editingId);
      await refreshStatusLog();
    } catch (err) {
      statusError = err instanceof ApiError ? err.message : 'Clear failed';
    } finally {
      statusClearing = false;
    }
  }

  async function onSave(e: Event) {
    e.preventDefault();
    formError = null;
    formOk = null;
    const trimmedName = name.trim();
    if (!trimmedName) {
      formError = 'Name is required.';
      return;
    }
    saving = true;
    try {
      if (editingId) {
        if (topTab === 'https') {
          const res = await updateIngressConnection(editingId, { name: trimmedName });
          connections = connections.map((c) => (c.id === editingId ? res.connection : c));
        } else {
          const p = Number(port);
          if (!host.trim() || !clientId.trim() || !p) {
            formError = 'Host, port and client id are required.';
            saving = false;
            return;
          }
          const res = await updateIngressConnection(editingId, {
            name: trimmedName,
            host: host.trim(),
            port: p,
            clientId: clientId.trim(),
            qos,
            username: username.trim() || null,
            password: password.trim() || undefined,
            caCert: topTab === 'mqtts' ? caCert.trim() || undefined : undefined,
            clientCert: topTab === 'mqtts' ? clientCert.trim() || undefined : undefined,
            clientKey: topTab === 'mqtts' ? clientKey.trim() || undefined : undefined,
          });
          connections = connections.map((c) => (c.id === editingId ? res.connection : c));
        }
        formOk = 'Saved.';
      } else if (topTab === 'https') {
        const res = await createIngressConnection({ name: trimmedName, type: 'https' });
        connections = [...connections, res.connection];
        formOk = 'Created.';
        resetForm();
      } else {
        const p = Number(port);
        if (!host.trim() || !clientId.trim() || !p) {
          formError = 'Host, port and client id are required.';
          saving = false;
          return;
        }
        const res = await createIngressConnection({
          name: trimmedName,
          type: topTab,
          host: host.trim(),
          port: p,
          clientId: clientId.trim(),
          qos,
          username: username.trim() || null,
          password: password.trim() || null,
          caCert: topTab === 'mqtts' ? caCert.trim() || null : null,
          clientCert: topTab === 'mqtts' ? clientCert.trim() || null : null,
          clientKey: topTab === 'mqtts' ? clientKey.trim() || null : null,
        });
        connections = [...connections, res.connection];
        formOk = 'Created.';
        resetForm();
      }
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Save failed';
    } finally {
      saving = false;
    }
  }

  async function onDelete(c: IngressConnectionSummary) {
    if (!window.confirm(`Delete connection "${c.name}"?`)) return;
    deleteError = null;
    busyDeleteId = c.id;
    try {
      await deleteIngressConnection(c.id);
      connections = connections.filter((x) => x.id !== c.id);
      if (editingId === c.id) resetForm();
    } catch (err) {
      deleteError = err instanceof ApiError ? err.message : 'Delete failed';
    } finally {
      busyDeleteId = null;
    }
  }

  function stateTone(state: string): 'success' | 'neutral' | 'warning' | 'danger' {
    if (state === 'connected') return 'success';
    if (state === 'connecting') return 'warning';
    if (state === 'error') return 'danger';
    return 'neutral';
  }
</script>

<div class="page">
  <a class="back" href="#/settings">← Settings</a>
  <h2>Ingress Connection Methods</h2>
  <p class="sub">
    Staff-only. Pre-create the MQTT/MQTTS broker settings or HTTPS URLs that Device Pools connect
    through. Unrelated to the client-side Connections page — separate table, separate purpose.
  </p>

  <div class="tabs">
    <button class="tab" class:active={topTab === 'https'} onclick={() => selectTab('https')}>HTTPS</button>
    <button class="tab" class:active={topTab === 'mqtts'} onclick={() => selectTab('mqtts')}>MQTTS</button>
    <button class="tab" class:active={topTab === 'mqtt'} onclick={() => selectTab('mqtt')}>MQTT</button>
  </div>

  {#snippet settingsCard()}
    <Card title={topTab === 'https' ? (editingId ? 'Edit connection' : 'New HTTPS connection') : `${topTab.toUpperCase()} Setting`}>
      {#if editingInUse}
        <p class="warn-banner">In use by a Device Pool — repoint it there before editing or deleting.</p>
        <dl class="detail-grid">
          <dt>Name</dt>
          <dd>{name}</dd>
          {#if topTab !== 'https'}
            <dt>Host</dt>
            <dd class="mono">{host}:{port}</dd>
            <dt>Client ID</dt>
            <dd class="mono">{clientId}</dd>
            <dt>QoS</dt>
            <dd>{qos}</dd>
          {:else}
            <dt>Endpoint</dt>
            <dd class="mono">{endpointUrl(editingConnection?.httpsEndpointSlug ?? '')}</dd>
          {/if}
        </dl>
        <div class="form-actions">
          <Button type="button" variant="secondary" disabled title="In use by a Device Pool — repoint it there before editing">
            Edit
          </Button>
        </div>
      {:else}
        <form class="form" onsubmit={(e) => void onSave(e)}>
          <label class="field">
            <span>Name</span>
            <input type="text" bind:value={name} maxlength="120" disabled={saving} required />
          </label>

          {#if topTab === 'https'}
            {#if editingConnection}
              <p class="field-hint">Endpoint: <span class="mono">{endpointUrl(editingConnection.httpsEndpointSlug ?? '')}</span></p>
            {:else}
              <p class="field-hint">A 5-character HTTPS-Endpoint-ID is generated automatically on create.</p>
            {/if}
          {:else}
            <div class="row2">
              <label class="field">
                <span>Host</span>
                <input type="text" bind:value={host} disabled={saving} required />
              </label>
              <label class="field">
                <span>Port</span>
                <input type="number" bind:value={port} min="1" max="65535" disabled={saving} required />
              </label>
            </div>
            <div class="row2">
              <label class="field">
                <span>Client ID</span>
                <input type="text" bind:value={clientId} disabled={saving} required />
              </label>
              <label class="field">
                <span>QoS</span>
                <select bind:value={qos} disabled={saving}>
                  <option value={0}>0 — At most once</option>
                  <option value={1}>1 — At least once</option>
                  <option value={2}>2 — Exactly once</option>
                </select>
              </label>
            </div>
            <label class="field">
              <span>Username (optional)</span>
              <input type="text" bind:value={username} disabled={saving} autocomplete="off" />
            </label>
            <label class="field">
              <span>Password (optional)</span>
              <input type="password" bind:value={password} disabled={saving} autocomplete="new-password" />
            </label>
            {#if topTab === 'mqtts'}
              <label class="field">
                <span>CA certificate (optional, PEM)</span>
                <textarea rows="2" bind:value={caCert} disabled={saving}></textarea>
              </label>
              <label class="field">
                <span>Client certificate (optional, PEM)</span>
                <textarea rows="2" bind:value={clientCert} disabled={saving}></textarea>
              </label>
              <label class="field">
                <span>Client private key (optional, PEM)</span>
                <textarea rows="2" bind:value={clientKey} disabled={saving}></textarea>
              </label>
            {/if}
          {/if}

          {#if formError}
            <p class="err" role="alert">{formError}</p>
          {/if}
          {#if formOk}
            <p class="ok" role="status">{formOk}</p>
          {/if}
          <div class="form-actions">
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create'}
            </Button>
            {#if editingId}
              <Button type="button" variant="ghost" disabled={saving} onclick={resetForm}>Cancel</Button>
            {/if}
            {#if editingId && topTab !== 'https'}
              <Button
                type="button"
                variant="danger"
                disabled={saving || busyDeleteId === editingId}
                onclick={() => editingConnection && void onDelete(editingConnection)}
              >
                {busyDeleteId === editingId ? '…' : 'Delete'}
              </Button>
            {/if}
          </div>
        </form>
      {/if}
    </Card>
  {/snippet}

  {#snippet statusLogCard()}
    <Card title={topTab === 'https' ? 'HTTPS status' : `${topTab.toUpperCase()} worker status`}>
      {#snippet actions()}
        <Button variant="ghost" size="sm" disabled={statusLoading} onclick={() => void refreshStatusLog()}>
          {statusLoading ? 'Refreshing…' : 'Refresh'}
        </Button>
        <Button
          variant="danger"
          size="sm"
          disabled={!editingId || statusClearing || statusEntries.length === 0}
          onclick={() => void onClearStatusLog()}
        >
          {statusClearing ? 'Clearing…' : 'Clear log'}
        </Button>
      {/snippet}
      <p class="muted small status-sub">
        {topTab === 'https' ? 'Last 50 rejected requests, latest first' : 'Last 50 entries, latest first'}
      </p>
      {#if statusError}
        <p class="err" role="alert">{statusError}</p>
      {/if}
      {#if !editingId}
        <p class="muted banner">No connection selected — nothing to show.</p>
      {:else if statusLoading && statusEntries.length === 0}
        <p class="muted">Loading…</p>
      {:else if statusEntries.length === 0}
        <p class="muted banner">
          {#if topTab === 'https'}
            No rejected requests yet.
          {:else}
            No status history yet — it fills in once the ingress worker attempts to connect (may need a restart
            after this connection was just created; see scripts/ingress_worker_ctl.py).
          {/if}
        </p>
      {:else}
        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>State</th>
                <th>Detail</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {#each statusEntries as entry (entry.id)}
                <tr>
                  <td><span class="state-badge" data-tone={stateTone(entry.state)}>{entry.state}</span></td>
                  <td>{entry.errorMessage ?? '—'}</td>
                  <td class="mono small">{new Date(entry.createdAt).toLocaleString()}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </Card>
  {/snippet}

  {#if error}
    <Card><p class="err" role="alert">{error}</p></Card>
  {:else if loading}
    <Card><p class="muted">Loading…</p></Card>
  {:else}
    {#if deleteError}
      <p class="err" role="alert">{deleteError}</p>
    {/if}

    {#if singleColumn}
      <div class="single-col">
        {@render settingsCard()}
      </div>
    {:else}
      <div class="grid">
        {@render settingsCard()}
        <Card title={topTab === 'https' ? 'HTTPS endpoints' : topTab === 'mqtts' ? 'MQTTS brokers' : 'MQTT brokers'} subtitle={`${currentList.length} configured`} padded={false}>
          {#if currentList.length === 0}
            <p class="banner muted">None yet.</p>
          {:else}
            <ul class="list">
              {#each currentList as c (c.id)}
                <li class:selected={editingId === c.id}>
                  <button class="row" onclick={() => void selectConnection(c)}>
                    <span class="name">{c.name}</span>
                    <span class="meta">
                      {#if c.type === 'https'}
                        {endpointUrl(c.httpsEndpointSlug ?? '')}
                      {:else}
                        {c.host}:{c.port}
                      {/if}
                    </span>
                  </button>
                  {#if c.type !== 'https'}
                    <span class="state-badge" data-tone={stateTone(c.connectionState)}>{c.connectionState}</span>
                  {/if}
                  <Button variant="ghost" size="sm" disabled={busyDeleteId === c.id} onclick={() => void onDelete(c)}>
                    {busyDeleteId === c.id ? '…' : 'Delete'}
                  </Button>
                </li>
              {/each}
            </ul>
          {/if}
        </Card>
      </div>
    {/if}

    {#if editingId}
      {@render statusLogCard()}
    {/if}
  {/if}
</div>

<style>
  .page {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    max-width: 72rem;
  }
  h2 {
    margin: 0.25rem 0 0;
    font-size: 1.5rem;
  }
  .back {
    font-size: 0.875rem;
    color: var(--text-muted);
    text-decoration: none;
  }
  .back:hover {
    color: var(--text);
  }
  .sub {
    margin: 0 0 0.5rem;
    color: var(--text-muted);
    font-size: 0.95rem;
    line-height: 1.45;
    max-width: 52rem;
  }
  .tabs {
    display: flex;
    gap: 0.5rem;
  }
  .tab {
    font: inherit;
    padding: 0.4rem 0.9rem;
    border-radius: 999px;
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--text-muted);
    cursor: pointer;
  }
  .tab.active {
    background: var(--accent, #2563eb);
    color: white;
    border-color: transparent;
  }
  .grid {
    display: grid;
    grid-template-columns: 1.4fr 1fr;
    gap: 1rem;
    align-items: start;
  }
  @media (max-width: 900px) {
    .grid {
      grid-template-columns: 1fr;
    }
  }
  .single-col {
    max-width: 40rem;
  }
  .warn-banner {
    margin: 0 0 0.75rem;
    padding: 0.65rem 0.75rem;
    border-radius: 8px;
    background: color-mix(in srgb, #f59e0b 12%, transparent);
    border: 1px solid color-mix(in srgb, #f59e0b 35%, transparent);
    color: var(--text);
    font-size: 0.9rem;
  }
  .status-sub {
    margin: -0.5rem 0 0.75rem;
  }
  .muted {
    color: var(--text-muted);
  }
  .err {
    color: #f87171;
    margin: 0.5rem 0 0;
    font-size: 0.9rem;
  }
  .ok {
    color: var(--success, #22c55e);
    margin: 0.5rem 0 0;
    font-size: 0.9rem;
  }
  .banner {
    padding: 1rem;
    margin: 0;
  }
  .list {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .list li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid var(--border);
  }
  .list li.selected {
    background: var(--surface-2, #f9fafb);
  }
  .row {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.15rem;
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
    text-align: left;
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }
  .name {
    font-weight: 600;
  }
  .meta {
    font-size: 0.8rem;
    color: var(--text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  .state-badge {
    font-size: 0.7rem;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    background: var(--surface-2, #eee);
    color: var(--text-muted);
    white-space: nowrap;
  }
  .state-badge[data-tone='success'] {
    background: color-mix(in srgb, var(--success, #22c55e) 18%, transparent);
    color: var(--success, #22c55e);
  }
  .state-badge[data-tone='warning'] {
    background: color-mix(in srgb, #f59e0b 18%, transparent);
    color: #f59e0b;
  }
  .state-badge[data-tone='danger'] {
    background: color-mix(in srgb, #f87171 18%, transparent);
    color: #f87171;
  }
  .form {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
  }
  .row2 {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 0.65rem;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    font-size: 0.9rem;
  }
  .field span {
    color: var(--text-muted);
    font-size: 0.8rem;
  }
  .field-hint {
    font-size: 0.75rem;
    color: var(--text-muted);
    opacity: 0.85;
  }
  .field input,
  .field textarea,
  .field select {
    font: inherit;
    padding: 0.45rem 0.6rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border);
    background: var(--surface-2, #fff);
    color: inherit;
    resize: vertical;
  }
  .form-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }
  .detail-grid {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 0.35rem 1rem;
    margin: 0 0 0.75rem;
  }
  .detail-grid dt {
    color: var(--text-muted);
    font-size: 0.85rem;
  }
  .detail-grid dd {
    margin: 0;
  }
  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  }
  .small {
    font-size: 0.8rem;
  }
  .table-wrap {
    overflow-x: auto;
  }
  .table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
  }
  th,
  td {
    text-align: left;
    padding: 0.55rem 0.4rem;
    border-bottom: 1px solid var(--border, #2a2a32);
    vertical-align: top;
  }
  th {
    color: var(--text-muted);
    font-weight: 500;
    font-size: 0.8rem;
  }
</style>
